import { useEffect, useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, ArrowRight, FileText, LayoutDashboard, ShieldCheck, Settings2, Microscope, FolderOpen, Loader2, Command } from 'lucide-react';
import { api } from '@/shared/api';

interface SearchHit {
  chunk_id: string;
  bap_id: string;
  bap_number: string | null;
  page_start: number;
  page_end: number;
  satker: string;
  score: number;
  snippet: string;
}

interface RecentBap {
  id: string;
  bap_number: string | null;
  satker: string;
  classification: string;
  status: string;
}

interface QuickAction {
  type: 'action';
  label: string;
  hint: string;
  path: string;
  icon: any;
}

const ACTIONS: QuickAction[] = [
  { type: 'action', label: 'Dashboard',  hint: 'KPI & analitik operasional',     path: '/dashboard', icon: LayoutDashboard },
  { type: 'action', label: 'Berkas',     hint: 'Daftar BAP terupload',           path: '/berkas',    icon: FolderOpen },
  { type: 'action', label: 'Analisa',    hint: 'Hub cross-doc + entitas',        path: '/analisa',   icon: Microscope },
  { type: 'action', label: 'Audit',      hint: 'Trail event hash-chain',         path: '/audit',     icon: ShieldCheck },
  { type: 'action', label: 'Administrasi', hint: 'User, regulasi, satker',       path: '/admin',     icon: Settings2 },
  { type: 'action', label: 'Tanya AI',   hint: 'Q&A chat dengan asisten',        path: '/tanya',     icon: FileText },
];

export default function CommandPalette({ open, onClose }: { open: boolean; onClose: () => void }) {
  const [query, setQuery] = useState('');
  const [hits, setHits] = useState<SearchHit[]>([]);
  const [recents, setRecents] = useState<RecentBap[]>([]);
  const [loading, setLoading] = useState(false);
  const [activeIdx, setActiveIdx] = useState(0);
  const inputRef = useRef<HTMLInputElement | null>(null);
  const debounceRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!open) return;
    setActiveIdx(0);
    inputRef.current?.focus();
    api<RecentBap[]>('/api/v1/dashboard/recent-bap?limit=5').then(setRecents).catch(() => {});
  }, [open]);

  useEffect(() => {
    if (!query.trim()) { setHits([]); return; }
    if (debounceRef.current) clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(async () => {
      setLoading(true);
      try {
        const resp = await fetch(`${import.meta.env.VITE_API_URL}/api/v1/analisa/search`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'X-Dev-User': 'admin@netra.local' },
          body: JSON.stringify({ query: query.trim(), limit: 8 }),
        });
        const data = await resp.json();
        setHits(data.hits || []);
      } finally {
        setLoading(false);
      }
    }, 250);
    return () => { if (debounceRef.current) clearTimeout(debounceRef.current); };
  }, [query]);

  // Compose result list
  const items: Array<{ kind: 'action' | 'hit' | 'recent'; data: any }> = [];
  const lowerQ = query.toLowerCase();
  if (query.trim()) {
    hits.forEach((h) => items.push({ kind: 'hit', data: h }));
    ACTIONS.filter((a) => a.label.toLowerCase().includes(lowerQ) || a.hint.toLowerCase().includes(lowerQ)).forEach((a) => items.push({ kind: 'action', data: a }));
  } else {
    ACTIONS.forEach((a) => items.push({ kind: 'action', data: a }));
    recents.forEach((r) => items.push({ kind: 'recent', data: r }));
  }

  const handlePick = (item: typeof items[number]) => {
    onClose();
    if (item.kind === 'action') navigate(item.data.path);
    else if (item.kind === 'hit') navigate(`/berkas/${item.data.bap_id}?view=ringkasan`);
    else if (item.kind === 'recent') navigate(`/berkas/${item.data.id}`);
  };

  const handleKey = (e: React.KeyboardEvent) => {
    if (e.key === 'ArrowDown') {
      e.preventDefault();
      setActiveIdx((i) => Math.min(items.length - 1, i + 1));
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      setActiveIdx((i) => Math.max(0, i - 1));
    } else if (e.key === 'Enter') {
      e.preventDefault();
      if (items[activeIdx]) handlePick(items[activeIdx]);
    } else if (e.key === 'Escape') {
      onClose();
    }
  };

  if (!open) return null;

  return (
    <div className="fixed inset-0 bg-ink/60 backdrop-blur z-[200] grid items-start justify-center pt-24 px-4" onClick={onClose}>
      <div
        className="bg-paper border-2 border-ink w-full max-w-2xl shadow-[0_10px_40px_-10px_hsl(var(--ink)/0.5)]"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center gap-3 px-4 py-3 border-b-2 border-ink">
          <Search className="w-4 h-4 text-brass" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => { setQuery(e.target.value); setActiveIdx(0); }}
            onKeyDown={handleKey}
            placeholder="Cari BAP, perintah, fakta lintas dokumen..."
            className="flex-1 bg-transparent font-mono text-[13px] text-ink outline-none placeholder:text-ink-3"
          />
          {loading && <Loader2 className="w-3.5 h-3.5 animate-spin text-brass" />}
          <kbd className="font-mono text-[10px] text-ink-3 border border-ink-line px-1.5 py-0.5">ESC</kbd>
        </div>

        <div className="max-h-[60vh] overflow-y-auto">
          {items.length === 0 && (
            <p className="px-4 py-8 font-mono text-xs text-ink-3 text-center italic">
              {loading ? 'Mencari...' : 'Tidak ada hasil.'}
            </p>
          )}

          {items.map((item, i) => {
            const active = i === activeIdx;
            const cls = `w-full text-left px-4 py-2.5 border-b border-dashed border-ink-line/40 last:border-b-0 transition flex items-start gap-3 ${
              active ? 'bg-brass-soft/50' : 'hover:bg-brass-soft/25'
            }`;

            if (item.kind === 'action') {
              const Icon = item.data.icon;
              return (
                <button key={`act-${item.data.path}`} onClick={() => handlePick(item)} className={cls}>
                  <Icon className="w-4 h-4 text-brass mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="font-display font-bold text-[13px] text-ink">{item.data.label}</div>
                    <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider">{item.data.hint}</div>
                  </div>
                  <ArrowRight className="w-3 h-3 text-ink-3" />
                </button>
              );
            }
            if (item.kind === 'hit') {
              const h = item.data as SearchHit;
              return (
                <button key={`hit-${h.chunk_id}`} onClick={() => handlePick(item)} className={cls}>
                  <FileText className="w-4 h-4 text-bluestamp mt-0.5" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-2">
                      <span className="font-display font-bold text-[13px] text-ink">{h.bap_number || h.bap_id.slice(0, 8)}</span>
                      <span className="font-mono text-[9px] text-ink-3 uppercase tracking-wider">Hal. {h.page_start}{h.page_end !== h.page_start ? `-${h.page_end}` : ''}</span>
                      <span className="font-mono text-[9px] text-brass ml-auto">{h.score.toFixed(3)}</span>
                    </div>
                    <p className="font-serif text-[11px] text-ink-2 leading-snug line-clamp-2 mt-0.5">{h.snippet}</p>
                  </div>
                  <ArrowRight className="w-3 h-3 text-ink-3" />
                </button>
              );
            }
            const r = item.data as RecentBap;
            return (
              <button key={`recent-${r.id}`} onClick={() => handlePick(item)} className={cls}>
                <FolderOpen className="w-4 h-4 text-ink-3 mt-0.5" />
                <div className="flex-1 min-w-0">
                  <div className="font-display font-bold text-[13px] text-ink">{r.bap_number || r.id.slice(0, 8)}</div>
                  <div className="font-mono text-[10px] text-ink-3 uppercase tracking-wider">{r.satker} · {r.classification} · {r.status}</div>
                </div>
                <ArrowRight className="w-3 h-3 text-ink-3" />
              </button>
            );
          })}
        </div>

        <div className="px-4 py-2 border-t-2 border-ink bg-paper-2/30 flex items-center gap-4 font-mono text-[10px] text-ink-3 uppercase tracking-wider">
          <span className="flex items-center gap-1">
            <kbd className="border border-ink-line px-1 py-0.5">↑↓</kbd>
            Navigate
          </span>
          <span className="flex items-center gap-1">
            <kbd className="border border-ink-line px-1 py-0.5">Enter</kbd>
            Select
          </span>
          <span className="flex items-center gap-1 ml-auto">
            <Command className="w-2.5 h-2.5" />
            Cross-doc search via FTS
          </span>
        </div>
      </div>
    </div>
  );
}
